"use client";

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { AuthUser, TokenResponse } from "./auth-api";
import type { ExecutionPatternHint } from "./api";
import { refreshToken as refreshTokenApi, logoutUser, getMe } from "./auth-api";
import { invalidateProjectionStores } from "./stores/projectionInvalidation";
import { useChatSessionStore } from "./stores/chatSession";

// ─── Auth Store ───────────────────────────────────────────────────────

const ACCESS_TOKEN_KEY = "de_access_token";
const REFRESH_TOKEN_KEY = "de_refresh_token";

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshTokenValue: string | null;
  loading: boolean;
  login: (tokens: TokenResponse) => void;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  initialize: () => void;
  /**
   * Patch the cached auth user so identity (e.g. display_name) stays in sync
   * across every surface immediately. Without this, a Settings rename updated
   * the Settings header but NOT the Home greeting until a reload/token refresh
   * (N3 split-brain). No-op when there is no logged-in user.
   */
  setUser: (patch: Partial<AuthUser>) => void;
}

export const useAuth = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  refreshTokenValue: null,
  loading: true,

  setUser: (patch: Partial<AuthUser>) => {
    set((state) =>
      state.user ? { user: { ...state.user, ...patch } } : {}
    );
  },

  login: (tokens: TokenResponse) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    useChatSessionStore.getState().clear();
    set({
      accessToken: tokens.access_token,
      refreshTokenValue: tokens.refresh_token,
      user: tokens.user,
    });
  },

  logout: async () => {
    const { refreshTokenValue } = get();
    if (refreshTokenValue) {
      try {
        await logoutUser(refreshTokenValue);
      } catch {
        // Ignore errors on logout
      }
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    useChatSessionStore.getState().clear();
    set({ accessToken: null, refreshTokenValue: null, user: null });
  },

  getToken: async (): Promise<string | null> => {
    const { accessToken, refreshTokenValue } = get();
    if (!accessToken || !refreshTokenValue) return null;

    // Try current access token
    try {
      const me = await getMe(accessToken);
      set({ user: me });
      return accessToken;
    } catch {
      // Access token expired — try refresh
    }

    try {
      const tokens = await refreshTokenApi(refreshTokenValue);
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      set({
        accessToken: tokens.access_token,
        refreshTokenValue: tokens.refresh_token,
        user: tokens.user,
      });
      return tokens.access_token;
    } catch {
      // Refresh also failed — clear state
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      set({ accessToken: null, refreshTokenValue: null, user: null });
      return null;
    }
  },

  initialize: () => {
    const storedAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);

    if (storedAccess && storedRefresh) {
      set({ accessToken: storedAccess, refreshTokenValue: storedRefresh });

      getMe(storedAccess)
        .then((me) => {
          set({ user: me, loading: false });
        })
        .catch(() => {
          refreshTokenApi(storedRefresh)
            .then((tokens) => {
              localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
              localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
              set({
                accessToken: tokens.access_token,
                refreshTokenValue: tokens.refresh_token,
                user: tokens.user,
                loading: false,
              });
            })
            .catch(() => {
              localStorage.removeItem(ACCESS_TOKEN_KEY);
              localStorage.removeItem(REFRESH_TOKEN_KEY);
              useChatSessionStore.getState().clear();
              set({
                accessToken: null,
                refreshTokenValue: null,
                user: null,
                loading: false,
              });
            });
        });
    } else {
      set({ loading: false });
    }
  },
}));

// ─── Data Refresh Store ──────────────────────────────────────────────
// Signals that components subscribe to for auto-refetching after a mutation.
// Each signal is a timestamp — when it changes, subscribers refetch.
//
// Cross-tab sync: a mutation in ONE tab must update EVERY open tab (the app
// is the user's external brain — it must never disagree with itself, even
// across tabs). The store is in-memory per-tab, so we bridge the three
// notifiers over a BroadcastChannel: each notify applies locally AND posts to
// the channel; the receiver applies locally WITHOUT re-posting (no echo loop).

type RefreshKind = "task" | "goal" | "schedule";

interface DataRefreshState {
  lastTaskChange: number;
  lastGoalChange: number;
  lastScheduleChange: number;
  notifyTaskChange: () => void;
  notifyGoalChange: () => void;
  notifyScheduleChange: () => void;
}

const DATA_SYNC_CHANNEL = "de-data-sync";

// One channel per tab. Guarded for SSR / browsers without BroadcastChannel.
const syncChannel: BroadcastChannel | null =
  typeof window !== "undefined" && "BroadcastChannel" in window
    ? new BroadcastChannel(DATA_SYNC_CHANNEL)
    : null;

export const useDataRefresh = create<DataRefreshState>((set) => {
  // Apply a change to THIS tab's store. Used both for local notifies and for
  // changes broadcast by other tabs.
  const applyLocal = (kind: RefreshKind) => {
    invalidateProjectionStores();
    const now = Date.now();
    if (kind === "task") set({ lastTaskChange: now });
    else if (kind === "goal") set({ lastGoalChange: now });
    else set({ lastScheduleChange: now });
  };

  if (syncChannel) {
    syncChannel.onmessage = (event: MessageEvent) => {
      const kind = event.data?.kind as RefreshKind | undefined;
      if (kind === "task" || kind === "goal" || kind === "schedule") {
        // Apply only — do NOT re-broadcast, or two tabs would echo forever.
        applyLocal(kind);
      }
    };
  }

  const notify = (kind: RefreshKind) => {
    applyLocal(kind);
    syncChannel?.postMessage({ kind });
  };

  return {
    lastTaskChange: 0,
    lastGoalChange: 0,
    lastScheduleChange: 0,
    notifyTaskChange: () => notify("task"),
    notifyGoalChange: () => notify("goal"),
    notifyScheduleChange: () => notify("schedule"),
  };
});

// ─── Carry-forward Re-entry Store ─────────────────────────────────────

export interface CarryForwardContextSummary {
  total: number;
  tomorrow_count: number;
  later_count: number;
  not_now_count: number;
  made_smaller_count: number;
  suggested_focus_title?: string | null;
  suggested_focus_kind?: "today" | "smaller" | "later" | null;
  execution_pattern_hint?: ExecutionPatternHint | null;
}

export interface CarryForwardPlannedFocus {
  blockId: string;
  title: string;
  date: string;
  taskId?: string | null;
  executionPatternHint?: ExecutionPatternHint | null;
}

interface CarryForwardContextState {
  summary: CarryForwardContextSummary | null;
  plannedFocus: CarryForwardPlannedFocus | null;
  setSummary: (summary: CarryForwardContextSummary) => void;
  clearSummary: () => void;
  clearSuggestedFocus: () => void;
  resolveSuggestedFocus: () => void;
  setPlannedFocus: (plannedFocus: CarryForwardPlannedFocus) => void;
  clearPlannedFocus: () => void;
}

export const useCarryForwardContext = create<CarryForwardContextState>()(
  persist(
    (set) => ({
      summary: null,
      plannedFocus: null,
      setSummary: (summary) => set({ summary }),
      clearSummary: () =>
        set((state) => ({
          summary: null,
          plannedFocus:
            state.plannedFocus && !state.plannedFocus.executionPatternHint && state.summary?.execution_pattern_hint
              ? {
                  ...state.plannedFocus,
                  executionPatternHint: state.summary.execution_pattern_hint,
                }
              : state.plannedFocus,
        })),
      clearSuggestedFocus: () =>
        set((state) => ({
          summary: state.summary
            ? {
                ...state.summary,
                suggested_focus_title: null,
                suggested_focus_kind: null,
              }
            : null,
        })),
      resolveSuggestedFocus: () =>
        set((state) => {
          const summary = state.summary;
          if (!summary) {
            return { summary: null };
          }

          let tomorrowCount = summary.tomorrow_count;
          let laterCount = summary.later_count;
          let smallerCount = summary.made_smaller_count;
          const notNowCount = summary.not_now_count;

          if (summary.suggested_focus_kind === "today" && tomorrowCount > 0) {
            tomorrowCount -= 1;
          } else if (summary.suggested_focus_kind === "smaller" && smallerCount > 0) {
            smallerCount -= 1;
          } else if (summary.suggested_focus_kind === "later" && laterCount > 0) {
            laterCount -= 1;
          }

          const nextTotal = tomorrowCount + laterCount + smallerCount + notNowCount;

          if (nextTotal <= 0) {
            return { summary: null };
          }

          return {
            summary: {
              ...summary,
              total: nextTotal,
              tomorrow_count: tomorrowCount,
              later_count: laterCount,
              made_smaller_count: smallerCount,
              suggested_focus_title: null,
              suggested_focus_kind: null,
            },
          };
        }),
      setPlannedFocus: (plannedFocus) =>
        set((state) => ({
          plannedFocus: {
            ...plannedFocus,
            executionPatternHint:
              plannedFocus.executionPatternHint ??
              state.summary?.execution_pattern_hint ??
              state.plannedFocus?.executionPatternHint ??
              null,
          },
        })),
      clearPlannedFocus: () => set({ plannedFocus: null }),
    }),
    {
      name: "de-carry-forward-context",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        summary: state.summary,
        plannedFocus: state.plannedFocus,
      }),
    }
  )
);

// ─── Goal Focus Context ───────────────────────────────────────────────

export interface GoalFocusTarget {
  goalId: string;
  goalTitle: string;
  headline?: string | null;
  message?: string | null;
  nextStep?: string | null;
  source?: "growth" | "schedule" | null;
}

interface GoalFocusState {
  target: GoalFocusTarget | null;
  setTarget: (target: GoalFocusTarget) => void;
  clearTarget: () => void;
}

export const useGoalFocusContext = create<GoalFocusState>()(
  persist(
    (set) => ({
      target: null,
      setTarget: (target) => set({ target }),
      clearTarget: () => set({ target: null }),
    }),
    {
      name: "de-goal-focus-context",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        target: state.target,
      }),
    }
  )
);

/**
 * Detect action types in agent response data and trigger refresh signals.
 * Called from ChatScreen.onResponse after every agent reply.
 */
export function detectAndNotifyActions(data: Record<string, unknown>) {
  const refresh = useDataRefresh.getState();

  // Check for task-related actions
  if (
    data.task_created || data.task_completed || data.task_skipped ||
    data.action === "task_created" || data.action === "task_completed"
  ) {
    refresh.notifyTaskChange();
  }

  // Check for goal-related actions
  if (
    data.goal_created || data.goal_updated ||
    data.action === "goal_created"
  ) {
    refresh.notifyGoalChange();
  }

  // Check for schedule-related actions
  if (
    data.schedule_generated || data.schedule_replanned ||
    data.action === "schedule_generated"
  ) {
    refresh.notifyScheduleChange();
  }

  // Intent-based detection as fallback
  const intent = (data.intent as string) || "";
  if (intent === "task_create" || intent === "task_decompose") {
    refresh.notifyTaskChange();
  }
  if (intent === "goal_create") {
    refresh.notifyGoalChange();
  }
  if (intent === "daily_planning") {
    refresh.notifyScheduleChange();
    refresh.notifyTaskChange();
  }
}

/**
 * Strict UAT 2026-05-24 — Chat Action Contract Layer.
 * Refreshes the downstream surfaces named in an AppActionResult so the
 * Goals/Plan/Today/Decisions UI reflects the mutation chat just made.
 *
 * Called from ChatScreen alongside `detectAndNotifyActions` whenever
 * the chat reply carries a top-level `action_result` payload.
 */
export interface ChatActionResultLike {
  success?: boolean;
  action?: string;
  refresh?: string[] | null;
  downstream_surface?: string | null;
}

export function notifyFromActionResult(result: ChatActionResultLike | null | undefined) {
  if (!result || !result.success) return;
  const refresh = useDataRefresh.getState();
  const surfaces = new Set<string>([
    ...((result.refresh || []) as string[]),
    ...(result.downstream_surface ? [result.downstream_surface as string] : []),
  ]);
  if (surfaces.has("goals")) refresh.notifyGoalChange();
  if (surfaces.has("today")) refresh.notifyTaskChange();
  if (surfaces.has("plan")) {
    refresh.notifyScheduleChange();
    refresh.notifyTaskChange();
  }
  if (surfaces.has("decisions")) {
    // No dedicated decision-change notifier yet — task/goal notifiers
    // both invalidate the Decide tab projection in practice, and the
    // Decisions sub-tab refetches on focus. Notify goals as the
    // closest available proxy so DecideScreen re-renders.
    refresh.notifyGoalChange();
  }
}

export function notifyFromActionReveal(
  reveal: { surface?: string | null } | null | undefined
) {
  if (!reveal?.surface) return;
  const refresh = useDataRefresh.getState();
  if (reveal.surface === "goals") refresh.notifyGoalChange();
  if (reveal.surface === "today") refresh.notifyTaskChange();
  if (reveal.surface === "plan") {
    refresh.notifyScheduleChange();
    refresh.notifyTaskChange();
  }
  if (reveal.surface === "decisions" || reveal.surface === "library") {
    refresh.notifyGoalChange();
  }
}

// ─── Theme Store ──────────────────────────────────────────────────────

type FlowMode = "warm" | "minimal" | "modern";

interface ThemeState {
  flowMode: FlowMode;
  setFlowMode: (m: FlowMode) => void;
  initialize: () => void;
}

function applyFlowMode(mode: FlowMode) {
  if (typeof document === "undefined") return;
  if (mode === "warm") {
    document.documentElement.removeAttribute("data-theme");
  } else {
    document.documentElement.setAttribute("data-theme", mode);
  }
}

export const useTheme = create<ThemeState>((set) => ({
  flowMode: "warm",

  setFlowMode: (m: FlowMode) => {
    localStorage.setItem("de_flow_mode", m);
    applyFlowMode(m);
    set({ flowMode: m });
  },

  initialize: () => {
    const savedFlow = localStorage.getItem("de_flow_mode") as FlowMode | null;
    if (savedFlow && ["warm", "minimal", "modern"].includes(savedFlow)) {
      applyFlowMode(savedFlow);
      set({ flowMode: savedFlow });
    } else {
      applyFlowMode("warm");
    }
  },
}));

// ── Pending capture intent — Reflect's "Try this" → Today Quick Capture handoff ──
// Reflect's SuggestionCard sets the intent + navigates to Today; TodayScreen
// reads it on mount, fires handleCapture(text), and clears. Spec §4.4.
export interface PendingCaptureIntent {
  prefillText: string;
  /** Optional context hint for the commit path so the source Recommendation
   *  can be marked accepted. */
  sourceContext?: { kind: "reflect_suggestion"; id: string };
}

interface PendingCaptureIntentState {
  intent: PendingCaptureIntent | null;
  setIntent: (intent: PendingCaptureIntent | null) => void;
  consumeIntent: () => PendingCaptureIntent | null;
}

export const usePendingCaptureIntent = create<PendingCaptureIntentState>((set, get) => ({
  intent: null,
  setIntent: (intent) => set({ intent }),
  consumeIntent: () => {
    const i = get().intent;
    if (i) set({ intent: null });
    return i;
  },
}));

export { useTodayProjectionStore } from "./stores/todayProjection";
export { usePlanProjectionStore } from "./stores/planProjection";
export { useReviewProjectionStore } from "./stores/reviewProjection";
export { useReflectProjectionStore } from "./stores/reflectProjection";
export { useAssistantContextStore } from "./stores/assistantContext";
